import { usePagination } from "@/components/ui/table-pagination";
import { Activity, FileText, Package } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Member, useMembersData } from "@/lib/settings/membersData";
import useTitanApp from "../../hooks/useTitanApp";
import { CompanySubscriptionType } from "../../../src/enum/CompanySubscriptionType.js";
import {
  updateCompanySubscription,
  getCompanyLedgerSummary,
  getCompanySubscriptions,
  getCompanyInvoicesByCompanyId,
  getCompnayUserCount,
  updateCompanySubscriptionSiteLicenses,
} from "../../../src/dispatchers/companyServiceBillingDispatchers.js";

/* -------------------------------------------------------------------------- */
/*                             Interface Definitions                          */
/* -------------------------------------------------------------------------- */

export interface ConsultationUsage {
  id: string;
  serviceId: string;
  serviceName: string;
  patientName: string;
  date: string;
  duration: number;
  price: number;
  type: "private" | "nhs";
  status: "completed" | "cancelled" | "pending";
}

interface SmsUsage {
  id: string;
  recipient: string;
  patientName: string;
  date: string;
  message: string;
  status: "sent" | "failed" | "pending";
  cost: number;
}

interface PromotionalCredit {
  id: string;
  amount: number;
  reason: string;
  appliedBy: string;
  date: string;
  notes?: string;
}

export interface BillingData {
  consultationUsage: ConsultationUsage[];
  smsUsage: SmsUsage[];
  promotionalCredits: PromotionalCredit[];
  invoices: Invoice[];
  planDetails: PlanDetails;
  currentPeriod: {
    startDate: string;
    endDate: string;
    totalConsultations: number;
    privateServicesTotal: number;
    nhsServicesTotal: number;
  };
}
export interface ConsultationUsage {
  id: string;
  serviceId: string;
  serviceName: string;
  patientName: string;
  date: string;
  duration: number;
  price: number;
  type: "private" | "nhs";
  status: "completed" | "cancelled" | "pending";
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  date: string;
  dueDate: string;
  amount: number;
  status: "paid" | "pending" | "overdue";
  consultationsCount: number;
  privateServicesTotal: number;
  nhsServicesTotal: number;
  downloadUrl: string;
  invoiceId: string;
  reference?: string;
  billingMonth?: number | null;
  billingYear?: number | null;
}

export interface InvoicesTableProps {
  invoices: Invoice[];
  isDarkMode?: boolean;
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

interface PlanDetails {
  userPacks: number; // Number of user packs purchased
  pricePerUserPack: number; // £25 per user pack
  usersPerPack: number; // 10 users per pack
  consultationFeePerConsultation: number; // £1.50 per consultation
  contractType: "Professional" | "Premium"; // Contract tier
  contractPeriod: {
    startDate: string;
    endDate: string;
  };
}

export interface BillingData {
  consultationUsage: ConsultationUsage[];
  invoices: Invoice[];
  planDetails: PlanDetails;
  currentPeriod: {
    startDate: string;
    endDate: string;
    totalConsultations: number;
    privateServicesTotal: number;
    nhsServicesTotal: number;
  };
}

/* -------------------------------------------------------------------------- */
/*                              Static Mock Data                              */
/* -------------------------------------------------------------------------- */

// export const initialBillingData: BillingData = {
//   promotionalCredits: [
//     {
//       id: "promo-1",
//       amount: 500.0,
//       reason: "Welcome Credit",
//       appliedBy: "Sarah Admin",
//       date: "2024-01-01T09:00:00Z",
//       notes: "Initial account setup promotional credit",
//     },
//     {
//       id: "promo-2",
//       amount: 150.0,
//       reason: "Service Issue Compensation",
//       appliedBy: "John Manager",
//       date: "2024-01-10T14:30:00Z",
//       notes: "Apology for service disruption on 2024-01-09",
//     },
//     {
//       id: "promo-3",
//       amount: 75.0,
//       reason: "Referral Bonus",
//       appliedBy: "Sarah Admin",
//       date: "2024-01-15T11:20:00Z",
//       notes: "Referral of 3 new pharmacy clients",
//     },
//   ],
//   smsUsage: [
//     {
//       id: "sms-1",
//       recipient: "+44 7700 900123",
//       patientName: "John Smith",
//       date: "2024-01-15T10:05:00Z",
//       message:
//         "Appointment confirmation: Your consultation is scheduled for tomorrow at 10:00 AM.",
//       status: "sent",
//       cost: 0.025,
//     },
//     {
//       id: "sms-2",
//       recipient: "+44 7700 900456",
//       patientName: "Sarah Johnson",
//       date: "2024-01-15T14:35:00Z",
//       message:
//         "Reminder: Please bring your vaccination card to your appointment.",
//       status: "sent",
//       cost: 0.025,
//     },
//     {
//       id: "sms-3",
//       recipient: "+44 7700 900789",
//       patientName: "Michael Brown",
//       date: "2024-01-16T09:20:00Z",
//       message: "Your prescription is ready for collection at the pharmacy.",
//       status: "sent",
//       cost: 0.025,
//     },
//     {
//       id: "sms-4",
//       recipient: "+44 7700 900321",
//       patientName: "Emily Davis",
//       date: "2024-01-16T11:10:00Z",
//       message:
//         "Appointment reminder: Your travel health consultation is tomorrow at 11:00 AM.",
//       status: "sent",
//       cost: 0.025,
//     },
//     {
//       id: "sms-5",
//       recipient: "+44 7700 900654",
//       patientName: "David Wilson",
//       date: "2024-01-17T13:50:00Z",
//       message: "Thank you for visiting. Please complete our feedback survey.",
//       status: "sent",
//       cost: 0.025,
//     },
//     {
//       id: "sms-6",
//       recipient: "+44 7700 900987",
//       patientName: "Lisa Thompson",
//       date: "2024-01-17T16:25:00Z",
//       message:
//         "Your blood pressure reading was normal. No further action required.",
//       status: "sent",
//       cost: 0.025,
//     },
//     {
//       id: "sms-7",
//       recipient: "+44 7700 900111",
//       patientName: "Robert Lee",
//       date: "2024-01-18T09:15:00Z",
//       message: "Appointment confirmed for 18/01/2024 at 2:30 PM. See you then!",
//       status: "sent",
//       cost: 0.025,
//     },
//     {
//       id: "sms-8",
//       recipient: "+44 7700 900222",
//       patientName: "Jennifer White",
//       date: "2024-01-18T14:40:00Z",
//       message:
//         "Your test results are ready. Please call the clinic to discuss.",
//       status: "sent",
//       cost: 0.025,
//     },
//   ],
//   planDetails: {
//     userPacks: 3, // 3 user packs purchased
//     pricePerUserPack: 25, // £25 per user pack
//     usersPerPack: 10, // 10 users per pack
//     consultationFeePerConsultation: 1.5, // £1.50 per consultation
//     contractType: "Professional", // Contract tier
//     contractPeriod: {
//       startDate: "2024-01-01",
//       endDate: "2024-12-31",
//     },
//   },
//   consultationUsage: [
//     {
//       id: "1",
//       serviceId: "s1",
//       serviceName: "General Consultation",
//       patientName: "John Smith",
//       date: "2024-01-15T10:00:00Z",
//       duration: 30,
//       price: 45.0,
//       type: "private",
//       status: "completed",
//     },
//     {
//       id: "2",
//       serviceId: "s2",
//       serviceName: "Vaccination Service",
//       patientName: "Sarah Johnson",
//       date: "2024-01-15T14:30:00Z",
//       duration: 15,
//       price: 0.0,
//       type: "nhs",
//       status: "completed",
//     },
//     {
//       id: "3",
//       serviceId: "s1",
//       serviceName: "General Consultation",
//       patientName: "Michael Brown",
//       date: "2024-01-16T09:15:00Z",
//       duration: 30,
//       price: 45.0,
//       type: "private",
//       status: "completed",
//     },
//     {
//       id: "4",
//       serviceId: "s3",
//       serviceName: "Travel Health Consultation",
//       patientName: "Emily Davis",
//       date: "2024-01-16T11:00:00Z",
//       duration: 45,
//       price: 65.0,
//       type: "private",
//       status: "completed",
//     },
//     {
//       id: "5",
//       serviceId: "s2",
//       serviceName: "Vaccination Service",
//       patientName: "David Wilson",
//       date: "2024-01-17T13:45:00Z",
//       duration: 15,
//       price: 0.0,
//       type: "nhs",
//       status: "completed",
//     },
//     {
//       id: "6",
//       serviceId: "s4",
//       serviceName: "Blood Pressure Check",
//       patientName: "Lisa Thompson",
//       date: "2024-01-17T16:20:00Z",
//       duration: 20,
//       price: 0.0,
//       type: "nhs",
//       status: "completed",
//     },
//   ],
//   invoices: [
//     {
//       id: "inv-1",
//       invoiceNumber: "INV-2024-001",
//       date: "2024-01-01",
//       dueDate: "2024-01-31",
//       amount: 340.0,
//       status: "paid",
//       consultationsCount: 8,
//       privateServicesTotal: 340.0,
//       nhsServicesTotal: 0.0,
//       downloadUrl: "/invoices/INV-2024-001.pdf",
//     },
//     {
//       id: "inv-2",
//       invoiceNumber: "INV-2024-002",
//       date: "2024-02-01",
//       dueDate: "2024-02-29",
//       amount: 455.0,
//       status: "paid",
//       consultationsCount: 12,
//       privateServicesTotal: 455.0,
//       nhsServicesTotal: 0.0,
//       downloadUrl: "/invoices/INV-2024-002.pdf",
//     },
//     {
//       id: "inv-3",
//       invoiceNumber: "INV-2024-003",
//       date: "2024-03-01",
//       dueDate: "2024-03-31",
//       amount: 520.0,
//       status: "pending",
//       consultationsCount: 14,
//       privateServicesTotal: 520.0,
//       nhsServicesTotal: 0.0,
//       downloadUrl: "/invoices/INV-2024-003.pdf",
//     },
//   ],
//   currentPeriod: {
//     startDate: "2024-01-01",
//     endDate: "2024-01-31",
//     totalConsultations: 6,
//     privateServicesTotal: 155.0,
//     nhsServicesTotal: 0.0,
//   },
// };

type BillingTabType = "current-period" | "subscriptions" | "invoices";

export const useBillingData = () => {
  const [activeTab, setActiveTab] = useState<BillingTabType>("current-period");
  const [isConsultationsExpanded, setIsConsultationsExpanded] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [updateUserPackLoading, setUpdateUserPackLoading] = useState(false);
  const [isSmsExpanded, setIsSmsExpanded] = useState(false);
  const [isPromotionsExpanded, setIsPromotionsExpanded] = useState(false);
  const [isConsultationsModalOpen, setIsConsultationsModalOpen] =
    useState(false);
  const invoicesPagination = usePagination(10);
  const { darkMode, appState } = useTitanApp();
  const { companyDetails, companyId } = appState;
  const { initialMembers } = useMembersData();
  const { companyServiceBillingState,locationState } = useTitanApp();
  
  const {
    companyServicesBilling,
    companySubscription,
    companyLedgerSummary,
    isCompanyLedgerSummaryloading,
    companyInvoices,
    iscompanyInvoicesloading,
    companyUserCount,
    companySubscriptionCosts
  } = companyServiceBillingState || {};
  const { companyPharmaciesCount } = locationState;

  const billingTabs = [
    {
      id: "current-period" as BillingTabType,
      label: "This billing period",
      icon: Activity,
    },
    {
      id: "subscriptions" as BillingTabType,
      label: "Subscriptions",
      icon: Package,
    },
    { id: "invoices" as BillingTabType, label: "Invoices", icon: FileText },
  ];

  // Utility functions for styling
  const getTextColor = (
    isDarkMode: boolean,
    variant: "primary" | "secondary" | "muted" = "primary",
  ) => {
    if (variant === "secondary")
      return isDarkMode ? "text-gray-300" : "text-gray-600";
    if (variant === "muted")
      return isDarkMode ? "text-gray-400" : "text-gray-500";
    return isDarkMode ? "text-white" : "text-gray-900";
  };

  const getCardStyle = (isDarkMode: boolean) =>
    `border rounded-lg transition-colors duration-300 ${
      isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
    }`;

  const getSubCardStyle = (isDarkMode: boolean) =>
    `border rounded-lg transition-colors duration-300 ${
      isDarkMode ? "border-gray-600 bg-gray-700" : "border-gray-200 bg-gray-50"
    }`;

  const getStatusBadgeStyle = (status: string) => {
    const baseClasses = "text-xs font-medium px-2.5 py-0.5 rounded";
    switch (status) {
      case "paid":
        return `${baseClasses} bg-green-100 text-green-800`;
      case "pending":
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-red-100 text-red-800`;
    }
  };
  const handleConfirmChanges = async (newLicenseCount: number) => {
    setUpdateUserPackLoading(true);
    // Calculate only the additional licenses (difference from previous)
    const previousLicenseCount = companySubscription?.totalSiteLicensesPurchased || 0;
    const additionalLicenses = newLicenseCount - previousLicenseCount;
    
    const companySubscriptionUpdateUserPacksModel = {
      _id: companySubscription?._id,
      siteLicences: additionalLicenses,
    };
    await updateCompanySubscriptionSiteLicenses(companySubscriptionUpdateUserPacksModel);
    await getCompanyLedgerSummary(companyId);
    await getCompanyInvoicesByCompanyId(companyId);
    await getCompnayUserCount(companyId);
    await getCompanySubscriptions(companyId);
    setUpdateUserPackLoading(false);
    setShowConfirmDialog(false);
  };
  return {
    darkMode,
    data: "",
    members: initialMembers,
    billingTabs,
    activeTab,
    setActiveTab,
    invoicesPagination,
    getTextColor,
    getCardStyle,
    getSubCardStyle,
    getStatusBadgeStyle,
    isConsultationsExpanded,
    setIsConsultationsExpanded,
    showConfirmDialog,
    setShowConfirmDialog,
    isSmsExpanded,
    setIsSmsExpanded,
    isPromotionsExpanded,
    setIsPromotionsExpanded,
    companyServicesBilling,
    companySubscription,
    companyLedgerSummary,
    isCompanyLedgerSummaryloading,
    companyInvoices,
    iscompanyInvoicesloading,
    CompanySubscriptionType,
    handleConfirmChanges,
    companyUserCount,
    companyDetails,
    setIsConsultationsModalOpen,
    isConsultationsModalOpen,
    updateUserPackLoading,
    companyPharmaciesCount,
    companySubscriptionCosts,
    companyId,
  };
};
